<template>
	<section class="provider-products-page">
		<div class="page-heading">
			<div>
				<h2>商品管理</h2>
				<p>查看店铺商品，管理商品审核及上下架状态</p>
			</div>
			<el-button type="primary" icon="el-icon-plus" @click="handlePublish">发布商品</el-button>
		</div>

		<div class="product-state-bar">
			<div class="state-tabs">
				<button
					v-for="item in stateTabs"
					:key="item.key"
					type="button"
					:class="['state-tab', { 'is-active': activeStateTab === item.key }]"
					@click="handleStateTabChange(item)"
				>
					{{ item.label }}
					<span v-if="item.key === 'selling' && activeStateTab === 'selling'">（{{ pageOptions.total }}）</span>
				</button>
			</div>
			<button
				type="button"
				:class="['draft-entry', { 'is-active': activeStateTab === draftTab.key }]"
				@click="handleStateTabChange(draftTab)"
			>
				<i class="el-icon-document"></i>
				<span>草稿箱</span>
			</button>
		</div>

		<div class="product-filter-card">
			<el-form :model="queryParams" class="product-search-form" label-position="top">
				<el-form-item label="商品名称">
					<el-input v-model.trim="queryParams.goodsTitle" clearable placeholder="请输入商品名称"></el-input>
				</el-form-item>
				<el-form-item label="商品类型">
					<el-select v-model="queryParams.goodsType" clearable placeholder="全部类型">
						<el-option label="平台自营商品" :value="1"></el-option>
						<el-option label="展销商品" :value="2"></el-option>
					</el-select>
				</el-form-item>
				<el-form-item label="上架时间">
					<el-date-picker
						v-model="shelfTimeRange"
						type="daterange"
						start-placeholder="开始日期"
						end-placeholder="结束日期"
						value-format="yyyy-MM-dd"
						range-separator="至"
						clearable
					></el-date-picker>
				</el-form-item>
				<el-form-item label="商品状态">
					<el-select v-model="queryParams.rowState" clearable placeholder="全部状态">
						<el-option v-for="item in stateOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
					</el-select>
				</el-form-item>
				<div class="search-actions">
					<el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
					<el-button icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
				</div>
			</el-form>
		</div>

		<div class="product-table-card">
			<div class="table-heading">
				<div>
					<strong>{{ activeTabLabel }}</strong>
					<span>共 {{ pageOptions.total }} 条商品记录</span>
				</div>
			</div>

			<el-table v-loading="loading" :data="tableData" border class="product-table" empty-text="暂无商品数据">
				<el-table-column label="商品信息" min-width="360">
					<template slot-scope="{ row }">
						<div class="product-info-cell">
							<el-image class="product-cover" :src="getGoodsImage(row)" fit="cover">
								<div slot="error" class="cover-placeholder"><i class="el-icon-picture-outline"></i></div>
							</el-image>
							<div class="product-copy">
								<strong :title="row.goodsTitle || row.title">{{ row.goodsTitle || row.title || '-' }}</strong>
								<span>订单编号：{{ row.orderCode || '-' }}</span>
								<el-button type="text" @click="handleDetail(row)">预览商品</el-button>
							</div>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="类目" min-width="135">
					<template slot-scope="{ row }">{{ categoryText(row) }}</template>
				</el-table-column>
				<el-table-column label="售价" width="115">
					<template slot-scope="{ row }">
						<span v-if="Number(row.priceType) === 2">面议</span>
						<strong v-else class="price-text">{{ priceText(row) }}</strong>
					</template>
				</el-table-column>
				<el-table-column label="累计销量" width="105" align="center">
					<template slot-scope="{ row }">{{ row.salesVolume || row.salesCount || row.saleCount || 0 }}</template>
				</el-table-column>
				<el-table-column label="商品状态" width="112" align="center">
					<template slot-scope="{ row }">
						<span :class="['status-pill', statusClass(row.rowState)]">{{ goodsStatusText(row) }}</span>
					</template>
				</el-table-column>
				<el-table-column label="订单审核状态" width="124" align="center">
					<template slot-scope="{ row }">
						<span :class="['status-pill', auditClass(row)]">{{ auditStatusText(row) }}</span>
					</template>
				</el-table-column>
				<el-table-column label="支付状态" width="108" align="center">
					<template slot-scope="{ row }">
						<span :class="['status-pill', orderStateClass(row.orderState)]">{{ orderStateText(row.orderState) }}</span>
					</template>
				</el-table-column>
				<el-table-column label="上架时间" min-width="160" align="center">
					<template slot-scope="{ row }">{{ timeText(row) }}</template>
				</el-table-column>
				<el-table-column label="操作" width="190" align="center">
					<template slot-scope="{ row }">
						<div class="row-actions">
							<el-button size="mini" type="primary" plain @click="handleDetail(row)">详情</el-button>
							<el-button v-if="Number(row.rowState) === 7" size="mini" plain @click="handleEdit(row)">编辑</el-button>
							<el-button v-if="Number(row.rowState) === 8" size="mini" plain @click="handleUpdateState(row, 9)">下架</el-button>
							<el-button
								v-if="[7, 9].includes(Number(row.rowState))"
								size="mini"
								type="danger"
								plain
								@click="handleUpdateState(row, 2)"
							>
								删除
							</el-button>
						</div>
					</template>
				</el-table-column>
			</el-table>

			<div class="product-pagination">
				<el-pagination
					background
					layout="total, sizes, prev, pager, next, jumper"
					:total="pageOptions.total"
					:current-page.sync="pageOptions.page"
					:page-size.sync="pageOptions.limit"
					:page-sizes="[10, 20, 50, 100]"
					@size-change="handleSizeChange"
					@current-change="handleCurrentChange"
				></el-pagination>
			</div>
		</div>
	</section>
</template>

<script>
	import { previewMerchantFile, queryMyMerchantGoodsPage, updateMerchantGoodsState } from '@/api/mall'

	const createQueryParams = () => ({
		goodsTitle: '',
		goodsType: '',
		rowState: ''
	})

	export default {
		name: 'ProviderProducts',
		data() {
			return {
				loading: false,
				tableData: [],
				pageOptions: {
					page: 1,
					limit: 20,
					total: 0
				},
				activeStateTab: 'all',
				shelfTimeRange: [],
				queryParams: createQueryParams(),
				// rowState 与旧版商品管理接口保持一致。
				stateTabs: [
					{ key: 'all', label: '全部', rowState: '' },
					{ key: 'selling', label: '售卖中', rowState: 8 },
					{ key: 'off', label: '已下架', rowState: 9 },
					{ key: 'auditing', label: '审核中', rowState: 1 },
					{ key: 'reject', label: '审核驳回', rowState: 3 },
					{ key: 'pass', label: '审核通过', rowState: 4 }
				],
				draftTab: { key: 'draft', label: '草稿箱', rowState: 7 },
				stateOptions: [
					{ label: '售卖中', value: 8 },
					{ label: '已下架', value: 9 },
					{ label: '审核中', value: 1 },
					{ label: '审核驳回', value: 3 },
					{ label: '审核通过', value: 4 },
					{ label: '草稿', value: 7 }
				]
			}
		},
		computed: {
			// 表格标题跟随当前状态页签变化。
			activeTabLabel() {
				const tabs = [...this.stateTabs, this.draftTab]
				const activeTab = tabs.find(item => item.key === this.activeStateTab)
				return activeTab ? activeTab.label : '全部'
			}
		},
		created() {
			this.loadData()
		},
		methods: {
			// 按旧版接口约定组装分页、筛选及上架时间区间参数。
			async loadData() {
				this.loading = true
				try {
					const params = {
						page: this.pageOptions.page,
						size: this.pageOptions.limit,
						...this.queryParams,
						startShelfTime: this.shelfTimeRange && this.shelfTimeRange[0] ? this.shelfTimeRange[0] : '',
						endShelfTime: this.shelfTimeRange && this.shelfTimeRange[1] ? this.shelfTimeRange[1] : ''
					}
					const response = await queryMyMerchantGoodsPage(params)
					const data = response.data || {}
					this.tableData = data.records || data.list || []
					this.pageOptions.total = Number(data.total || this.tableData.length || 0)
				} finally {
					this.loading = false
				}
			},
			handleSearch() {
				this.pageOptions.page = 1
				this.loadData()
			},
			handleReset() {
				this.queryParams = createQueryParams()
				this.shelfTimeRange = []
				this.activeStateTab = 'all'
				this.pageOptions.page = 1
				this.pageOptions.limit = 20
				this.loadData()
			},
			handleSizeChange(size) {
				this.pageOptions.page = 1
				this.pageOptions.limit = size
				this.loadData()
			},
			handleCurrentChange(page) {
				this.pageOptions.page = page
				this.loadData()
			},
			handleStateTabChange(item) {
				this.activeStateTab = item.key
				this.queryParams.rowState = item.rowState
				this.pageOptions.page = 1
				this.loadData()
			},
			handlePublish() {
				this.$router.push('/provider/product-publish')
			},
			handleDetail(row) {
				this.$router.push({ path: '/provider/product-publish', query: { id: row.id, readonly: 1 } })
			},
			handleEdit(row) {
				this.$router.push({ path: '/provider/product-publish', query: { id: row.id } })
			},
			// 商品下架和删除共用状态修改接口，执行前增加二次确认。
			handleUpdateState(row, rowState) {
				const isDelete = rowState === 2
				const actionText = isDelete ? '删除' : '下架'
				this.$modal
					.confirm(`确定${actionText}商品“${row.goodsTitle || row.title || '-'}”吗？`)
					.then(async () => {
						await updateMerchantGoodsState({ id: row.id, rowState })
						this.$message.success(isDelete ? '删除成功' : '下架成功')
						this.loadData()
					})
					.catch(() => {})
			},
			getGoodsImage(row) {
				const image = row.mainImage || row.mainImageId || row.coverFileId || row.fileRecordId || ''
				if (!image) return ''
				if (/^https?:\/\//.test(image) || image.indexOf('data:image') === 0) return image
				return previewMerchantFile() + image
			},
			categoryText(row) {
				return row.categoryThirdName || row.categorySecondName || row.categoryFirstName || row.categoryName || '-'
			},
			priceText(row) {
				if (row.sellingPrice === undefined || row.sellingPrice === null || row.sellingPrice === '') return '-'
				return `¥ ${row.sellingPrice}`
			},
			goodsStatusText(row) {
				if (Number(row.rowState) === 8) return '售卖中'
				if (Number(row.rowState) === 9) return '已下架'
				if (Number(row.rowState) === 7) return '草稿'
				return this.rowStateText(row.rowState)
			},
			auditStatusText(row) {
				if (row.auditState === undefined || row.auditState === null || row.auditState === '') return '-'
				return { 10: '待审核', 1: '审核通过', 2: '审核不通过' }[Number(row.auditState)] || '-'
			},
			statusClass(value) {
				const state = Number(value)
				if (state === 8) return 'is-success'
				if (state === 9) return 'is-off'
				if (state === 7) return 'is-draft'
				if (state === 3) return 'is-danger'
				return 'is-warning'
			},
			auditClass(row) {
				if (row.auditState === undefined || row.auditState === null || row.auditState === '') return 'is-draft'
				const state = Number(row.auditState)
				if (state === 1) return 'is-success'
				if (state === 2) return 'is-danger'
				if (state === 10) return 'is-warning'
				return 'is-draft'
			},
			orderStateText(value) {
				const state = String(value === undefined || value === null ? '' : value).toUpperCase()
				return { '0': '待支付', O: '待支付', '1': '已支付', '2': '已取消' }[state] || '-'
			},
			orderStateClass(value) {
				const state = String(value === undefined || value === null ? '' : value).toUpperCase()
				if (state === '1') return 'is-success'
				if (state === '2') return 'is-off'
				if (state === '0' || state === 'O') return 'is-warning'
				return 'is-draft'
			},
			timeText(row) {
				const value = row.shelfTime || row.publishTime || row.createTime || ''
				return value ? String(value) : '-'
			},
			rowStateText(value) {
				const stateTextMap = {
					1: '待审核',
					2: '已删除',
					3: '审核驳回',
					4: '审核通过',
					7: '草稿',
					8: '已上架',
					9: '已下架'
				}
				return stateTextMap[Number(value)] || '待审核'
			}
		}
	}
</script>

<style lang="scss" scoped>
	.provider-products-page {
		min-height: 100%;
		padding: 24px;
		border: 1px solid #e4e9ef;
		border-radius: 8px;
		background: #ffffff;
		box-sizing: border-box;
	}

	.page-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding-bottom: 19px;
		border-bottom: 1px solid #e9edf2;

		h2 {
			margin: 0 0 6px;
			color: #303846;
			font-size: 21px;
		}
		p {
			margin: 0;
			color: #929aa6;
			font-size: 13px;
		}
	}

	.product-state-bar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-top: 18px;
		border-bottom: 1px solid #dce5ee;
	}

	.state-tabs {
		display: flex;
		align-items: center;
	}

	.state-tab,
	.draft-entry {
		position: relative;
		height: 48px;
		padding: 0 16px;
		border: none;
		color: #566273;
		font-size: 14px;
		font-weight: 500;
		background: transparent;
		cursor: pointer;
		transition: color 0.2s ease, background-color 0.2s ease;

		&::after {
			position: absolute;
			right: 16px;
			bottom: -1px;
			left: 16px;
			height: 3px;
			border-radius: 3px 3px 0 0;
			background: transparent;
			content: '';
		}
		&:hover,
		&.is-active {
			color: #2671cb;
			background: #f5f9fe;
		}
		&.is-active::after {
			background: #2671cb;
		}
	}

	.draft-entry {
		display: flex;
		align-items: center;

		i {
			margin-right: 6px;
			font-size: 17px;
		}
	}

	.product-filter-card {
		margin-top: 16px;
		padding: 17px 18px;
		border: 1px solid #e4ebf3;
		border-radius: 7px;
		background: #f8fbfe;
	}

	.product-search-form {
		display: grid;
		grid-template-columns: minmax(170px, 1fr) minmax(160px, 0.8fr) minmax(290px, 1.35fr) minmax(160px, 0.8fr) auto;
		align-items: end;
		gap: 14px;

		::v-deep .el-form-item {
			margin-bottom: 0;
		}
		::v-deep .el-form-item__label {
			padding-bottom: 6px;
			color: #526072;
			font-weight: 500;
			line-height: 20px;
		}
		::v-deep .el-select,
		::v-deep .el-date-editor {
			width: 100%;
		}
	}

	.search-actions {
		display: flex;
		padding-bottom: 1px;
		white-space: nowrap;
	}

	.product-table-card {
		margin-top: 16px;
		padding: 18px;
		border: 1px solid #e4e9ef;
		border-radius: 7px;
	}

	.table-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 15px;

		strong {
			color: #303b4c;
			font-size: 17px;
		}
		span {
			margin-left: 12px;
			color: #969faa;
			font-size: 13px;
		}
	}

	.product-table {
		width: 100%;

		::v-deep th {
			height: 46px;
			padding: 0;
			color: #344154;
			background: #f2f7fc;
		}
		::v-deep td {
			border-color: #e3eaf1;
		}
		::v-deep .el-table__body td {
			padding: 11px 0;
		}
		::v-deep .el-table__row:hover > td {
			background: #f8fbff;
		}
	}

	.product-info-cell {
		display: flex;
		align-items: center;
		padding-left: 4px;
		text-align: left;
	}

	.product-cover {
		width: 104px;
		height: 76px;
		margin-right: 14px;
		flex-shrink: 0;
		border: 1px solid #dce5ee;
		border-radius: 5px;
		background: #f5f7fa;
	}

	.cover-placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		color: #bdc5cf;
		font-size: 27px;
	}

	.product-copy {
		min-width: 0;

		> strong {
			display: block;
			max-width: 235px;
			overflow: hidden;
			color: #2f3b4c;
			font-size: 15px;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
		> span {
			display: block;
			margin: 7px 0 3px;
			color: #8893a1;
			font-size: 12px;
		}
		.el-button {
			padding: 2px 0;
			font-size: 12px;
		}
	}

	.price-text {
		color: #e56a2f;
		font-weight: 600;
	}

	.status-pill {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 62px;
		height: 26px;
		padding: 0 9px;
		border-radius: 13px;
		font-size: 12px;
		font-weight: 600;
		box-sizing: border-box;

		&.is-success {
			color: #168653;
			background: #e5f6ed;
		}
		&.is-warning {
			color: #ae741d;
			background: #fff2da;
		}
		&.is-danger {
			color: #d34e4e;
			background: #feebeb;
		}
		&.is-off,
		&.is-draft {
			color: #748090;
			background: #edf0f3;
		}
	}

	.row-actions {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;

		.el-button {
			margin-left: 0;
		}
	}

	.product-pagination {
		display: flex;
		justify-content: center;
		margin-top: 22px;
	}

	@media (max-width: 1360px) {
		.product-search-form {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
		.search-actions {
			align-self: end;
		}
	}
</style>
